import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";
import { useSocialMediaMeta } from "@/hooks/useSocialMediaMeta";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Heart, MapPin, User } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

type ConfirmedDonor = Tables<"confirmed_donors">;

export default function MeetOurDonors() {
  const [donors, setDonors] = useState<ConfirmedDonor[]>([]);
  const [loading, setLoading] = useState(true);
  useSocialMediaMeta({
    title: "Meet Our Individual Donors - Bobo & Teedee®",
    description: "Thank you to our wonderful community of individual supporters making educational entertainment accessible to all children."
  });

  useEffect(() => {
    const fetchDonors = async () => {
      try {
        const { data, error } = await supabase
          .from("confirmed_donors")
          .select("*")
          .eq("is_active", true)
          .order("display_order", { ascending: true });

        if (error) throw error;
        setDonors(data || []);
      } catch (error) {
        console.error("Error fetching donors:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDonors();
  }, []);

  const getDisplayName = (donor: ConfirmedDonor) => {
    if (donor.recognition_level === 'anonymous') return 'Anonymous Supporter';
    if (donor.recognition_level === 'initials_only' && donor.donor_name) {
      const names = donor.donor_name.split(' ');
      return names.map(name => name.charAt(0).toUpperCase()).join('. ') + '.';
    }
    if (donor.recognition_level === 'first_name_only' && donor.donor_name) {
      return donor.donor_name.split(' ')[0];
    }
    return donor.display_name || donor.donor_name || 'Anonymous Supporter';
  };

  // Group donors by tier
  const donorsByTier = donors.reduce((acc, donor) => {
    const tier = donor.donation_tier || 'Community Supporter';
    if (!acc[tier]) acc[tier] = [];
    acc[tier].push(donor);
    return acc;
  }, {} as Record<string, ConfirmedDonor[]>);

  const tierOrder = ['Platinum Supporter', 'Gold Supporter', 'Silver Supporter', 'Bronze Supporter', 'Community Supporter'];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-secondary/5">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <div className="animate-pulse space-y-4">
              <div className="h-12 bg-muted rounded w-96 mx-auto"></div>
              <div className="h-6 bg-muted rounded w-64 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <Helmet>
        <title>Meet Our Individual Donors - Bobo & Teedee®</title>
        <meta name="description" content="Thank you to our wonderful community of individual supporters making educational entertainment accessible to all children." />
        <meta property="og:title" content="Meet Our Individual Donors - Bobo & Teedee®" />
        <meta property="og:description" content="Thank you to our wonderful community of individual supporters making educational entertainment accessible to all children." />
        <meta property="og:url" content={`${window.location.origin}/meet-our-donors`} />
        <meta property="og:image" content={`${window.location.origin}/img/social/og-default.png`} />
        <meta name="twitter:title" content="Meet Our Individual Donors - Bobo & Teedee®" />
        <meta name="twitter:description" content="Thank you to our wonderful community of individual supporters making educational entertainment accessible to all children." />
        <meta name="twitter:image" content={`${window.location.origin}/img/social/twitter-default.png`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-secondary/5">
        <div className="container mx-auto px-4 py-16">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-secondary via-primary to-accent bg-clip-text text-transparent">
              Meet Our Individual Donors
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              These wonderful individuals have chosen to support educational entertainment for children. 
              Their generosity helps us create content that inspires young minds across the Cayman Islands and beyond.
            </p>
            <Link to="/support">
              <Button size="lg" className="gap-2">
                <Heart className="h-5 w-5" />
                Join Our Community
              </Button>
            </Link>
          </div>

          {/* Donors by Tier */}
          {Object.keys(donorsByTier).length === 0 ? (
            <div className="text-center py-16">
              <Heart className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-2xl font-semibold mb-4">Be Our First Individual Supporter!</h3>
              <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
                We're building a community of individuals who believe in the power of educational entertainment. 
                Your name could be the first to appear on this wall of thanks!
              </p>
              <Link to="/support">
                <Button size="lg">Make a Donation</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-16">
              {tierOrder.map((tierName) => {
                const tierDonors = donorsByTier[tierName];
                if (!tierDonors || tierDonors.length === 0) return null;

                return (
                  <div key={tierName} className="space-y-8">
                    <div className="text-center">
                      <Badge variant="secondary" className="text-lg px-4 py-2 mb-4">
                        {tierName}s ({tierDonors.length})
                      </Badge>
                    </div>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {tierDonors.map((donor) => (
                        <Card 
                          key={donor.id} 
                          className="group hover:shadow-lg transition-all duration-300 border hover:border-secondary/30"
                        >
                          <CardHeader className="text-center pb-3">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-secondary to-primary flex items-center justify-center mx-auto mb-3">
                              <User className="h-6 w-6 text-white" />
                            </div>
                            <CardTitle className="text-lg group-hover:text-secondary transition-colors">
                              {getDisplayName(donor)}
                            </CardTitle>
                            {donor.location && donor.recognition_level !== 'anonymous' && (
                              <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
                                <MapPin className="h-3 w-3" />
                                {donor.location}
                              </div>
                            )}
                            {donor.is_recurring && (
                              <Badge variant="outline" className="text-xs">
                                Recurring Supporter
                              </Badge>
                            )}
                          </CardHeader>
                          
                          {donor.message && donor.recognition_level !== 'anonymous' && (
                            <CardContent className="pt-0">
                              <blockquote className="text-sm italic text-muted-foreground text-center border-l-2 border-secondary/20 pl-3">
                                "{donor.message}"
                              </blockquote>
                            </CardContent>
                          )}
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Statistics */}
          <div className="mt-16 pt-16 border-t">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-3xl font-bold text-secondary mb-2">
                  {donors.length}
                </div>
                <div className="text-muted-foreground">Individual Supporters</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-secondary mb-2">
                  {donors.filter(d => d.is_recurring).length}
                </div>
                <div className="text-muted-foreground">Recurring Donors</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-secondary mb-2">
                  {Object.keys(donorsByTier).length}
                </div>
                <div className="text-muted-foreground">Support Levels</div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-16 pt-16 border-t">
            <h3 className="text-2xl font-semibold mb-4">Join This Amazing Community</h3>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Every contribution, no matter the size, makes a difference in a child's educational journey. 
              Become part of our mission today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/support">
                <Button size="lg">Make a Donation</Button>
              </Link>
              <Link to="/meet-our-sponsors">
                <Button variant="outline" size="lg">Meet Our Corporate Sponsors</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}